// ════════════════════════════════════════════════════════════════
// GSA_CONSTANTS.js
// All data constants for the Guided Self-Assessment.
// Ported from prototype data.js into ES module named exports.
// ════════════════════════════════════════════════════════════════

var subDomain = "api";
if (window.location.host !== "www.startfreept.com" && window.location.host !== "startfreept.com" && window.location.host !== "virdiohealth.com" && window.location.host !== "www.virdiohealth.com") {
  subDomain = "uatapi";
}


export const SCORE_API_URL = "https://" + subDomain + ".virdiohealth.com/api/v1/assessment/analyze";

// ── Asset URLs ────────────────────────────────────────────────
export const LOGO = "https://virdiodev.s3.us-east-2.amazonaws.com/sampleVideo/startfreept_logo.svg";
export const SKELETON = "https://virdiodev.s3.us-east-2.amazonaws.com/sampleVideo/VH-Bodyoutline.png";
export const PLACEHOLDER_VIDEO = "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/PLACEHOLDER.mp4";
export const HOME_VIDEO_2 = "https://virdiodev.s3.us-east-2.amazonaws.com/sampleVideo/HeroVideo-StartFreePT-v2.0.mp4";

// ════════════════════════════════════════════════════════════════
// PROBLEM AREAS & CLINICAL PATHS
// LBP = unique 2-step CPG branching (leg pain? → then CPG)
// HKT = unified structure: cpgQuestion → subtypeQuestions → tests
// ════════════════════════════════════════════════════════════════
/** Same “Coming Soon” body areas as GSA v1 problem area screen. */
export const COMING_SOON_OPTIONS = ["Shoulder", "Elbow", "Ankle", "Wrist", "Neck", "Flexibility"];
export const CLINICAL_PATHS = {
  LBP: {
    name: "Lower Back",
    zones: { left: ['Lower Back', 'Buttock', 'Thigh', 'Lower Leg'], right: ['Lower Back', 'Buttock', 'Thigh', 'Lower Leg'] },
  },
  HIP: {
    name: "Hip",
    zones: { left: ['Lower Back', 'Buttock', 'Front/Groin', 'Side Hip', 'Thigh', 'Knee'], right: ['Lower Back', 'Buttock', 'Front/Groin', 'Side Hip', 'Thigh', 'Knee'] },
    overrideTarget: 'PELVIC_CONTROL_IMPAIRMENT',
    cpgQuestion: {
      prompt: "Which of these best describes your primary hip complaint?",
      options: [
        {
          id: "hip-cpg-mobility",
          label: "A feeling of stiffness, tightness, or a block in movement.",
          value: "HIP_MOBILITY_DEFICITS",
          cpgForBackend: "Hip Pain with Mobility Deficits",
          routesTo: "subtypeQuestion",
          subtypeQuestionIndex: 0,
        },
        {
          id: "hip-cpg-power",
          label: "A feeling of weakness, fatigue, or that the hip might 'give out'.",
          value: "HIP_MUSCLE_POWER_DEFICITS",
          cpgForBackend: "Hip Pain with Muscle Power Deficits",
          routesTo: "subtypeQuestion",
          subtypeQuestionIndex: 1,
        },
        {
          id: "hip-cpg-motor",
          label: "A pinching, clicking, or catching feeling deep in the front of the hip.",
          value: "HIP_IMPAIRED_MOTOR_CONTROL",
          cpgForBackend: "Hip Pain with Impaired Motor Control",
          routesTo: "direct",
          directSubtype: "PELVIC_CONTROL_IMPAIRMENT",
        },
      ],
    },
    subtypeQuestions: [
      {
        prompt: "Where do you feel the tightness the most?",
        options: [
          { id: "hip-sub-lateral", label: "On the side of my hip, especially when lying on that side or walking.", value: "HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT" },
          { id: "hip-sub-anterior", label: "In the front of my hip or groin, especially when extending my leg behind me.", value: "ANTERIOR_CHAIN_MOBILITY_DEFICIT" },
          { id: "hip-sub-posterior", label: "In my buttock or the back of my hip, especially when bringing my knee towards my chest.", value: "POSTERIOR_CHAIN_MOBILITY_DEFICIT" },
          { id: "hip-sub-rotational", label: "In my groin or the crease where my leg meets my body, especially with twisting or crossing my legs.", value: "POSTERIOR_CHAIN_MOBILITY_DEFICIT" },
          { id: "hip-sub-neuro", label: "A specific, deep pain in my buttock, sometimes with tingling down my leg.", value: "NEUROMUSCULAR_MOBILITY_DEFICIT" },
          { id: "hip-sub-global", label: "A general, deep ache and stiffness all around the joint, worse in the morning.", value: "POSTERIOR_CHAIN_MOBILITY_DEFICIT" },
        ],
      },
      {
        prompt: "Which activity is most difficult or brings on your symptoms?",
        options: [
          { id: "hip-sub-adductor", label: "Moving my legs apart or a feeling of strain in my inner thigh/groin.", value: "ADDUCTOR_OVERLOAD_CONTROL_DEFICIT" },
          { id: "hip-sub-abduction", label: "Standing on one leg, climbing stairs, or feeling my hip drop when I walk.", value: "HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT" },
        ],
      },
    ],
  },
  THIGH: {
    name: "Thigh",
    zones: { left: ['Back', 'Buttock', 'Hip', 'Back Thigh', 'Front Thigh', 'Inner Thigh', 'Outer Thigh', 'Knee'], right: ['Back', 'Buttock', 'Hip', 'Back Thigh', 'Front Thigh', 'Inner Thigh', 'Outer Thigh', 'Knee'] },
    overrideTarget: 'HAMSTRING_LOADING_LENGTHENING_DEFICIT',
    cpgQuestion: {
      prompt: "Which of these best describes the feeling in your thigh?",
      options: [
        {
          id: "thigh-cpg-muscular",
          label: "A muscular ache, pull, or strain.",
          value: "THIGH_MUSCULAR_ORIGIN",
          cpgForBackend: "Thigh Pain with Muscular Origin",
          routesTo: "subtypeQuestion",
          subtypeQuestionIndex: 0,
        },
        {
          id: "thigh-cpg-neural",
          label: "A burning, tingling, or 'pins and needles' feeling.",
          value: "THIGH_NEURAL_INVOLVEMENT",
          cpgForBackend: "Thigh Pain with Muscular Origin",
          routesTo: "direct",
          directSubtype: "HAMSTRING_LOADING_LENGTHENING_DEFICIT",
        },
        {
          id: "thigh-cpg-referred",
          label: "A vague, deep ache that seems to come from my back or hip.",
          value: "THIGH_REFERRED_SYMPTOMS",
          cpgForBackend: "Thigh Pain with Muscular Origin",
          routesTo: "direct",
          directSubtype: "HAMSTRING_LOADING_LENGTHENING_DEFICIT",
        },
      ],
    },
    subtypeQuestions: [
      {
        prompt: "Where in your thigh do you feel it the most?",
        options: [
          { id: "thigh-sub-anterior", label: "The front of my thigh.", value: "QUADRICEPS_LOADING_IMPACT_INTOLERANCE" },
          { id: "thigh-sub-medial", label: "The inner thigh or groin area.", value: "ADDUCTOR_OVERLOAD_CONTROL_DEFICIT" },
          { id: "thigh-sub-posterior", label: "The back of my thigh.", value: "HAMSTRING_LOADING_LENGTHENING_DEFICIT" },
          { id: "thigh-sub-lateral", label: "The outer (lateral) side of my thigh.", value: "ITB_FRICTION_SYNDROME" },
        ],
      },
    ],
  },
  KNEE: {
    name: "Knee",
    zones: { left: ['Back', 'Buttock', 'Hip', 'Thigh', 'Front Knee', 'Back Knee', 'Inner Knee', 'Outer Knee'], right: ['Back', 'Buttock', 'Hip', 'Thigh', 'Front Knee', 'Back Knee', 'Inner Knee', 'Outer Knee'] },
    overrideTarget: 'LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY',
    cpgQuestion: {
      prompt: "Which of these best describes where you feel your knee pain?",
      options: [
        {
          id: "knee-cpg-pfp",
          label: "Around or behind my kneecap.",
          value: "KNEE_PATELLOFEMORAL",
          cpgForBackend: "Patellofemoral Pain (PFP)",
          routesTo: "subtypeQuestion",
          subtypeQuestionIndex: 0,
        },
        {
          id: "knee-cpg-mobility",
          label: "The entire joint feels stiff, blocked, or unstable.",
          value: "KNEE_MOBILITY_STABILITY",
          cpgForBackend: "Knee Pain with Mobility/Stability Deficits",
          routesTo: "subtypeQuestion",
          subtypeQuestionIndex: 1,
        },
        {
          id: "knee-cpg-lateral",
          label: "On the outer (lateral) side of my knee.",
          value: "KNEE_LATERAL",
          cpgForBackend: "Knee Pain with Lateral Symptoms",
          routesTo: "direct",
          directSubtype: "ITB_FRICTION_SYNDROME",
        },
        {
          id: "knee-cpg-posterior",
          label: "In the back of my knee, especially when bending or straightening.",
          value: "KNEE_POSTERIOR",
          cpgForBackend: "Knee Pain with Posterior Symptoms",
          routesTo: "direct",
          directSubtype: "HAMSTRING_LOADING_LENGTHENING_DEFICIT",
        },
        {
          id: "knee-cpg-medial",
          label: "On the inner (medial) side of my knee.",
          value: "KNEE_MEDIAL",
          cpgForBackend: "Knee Pain with Medial Symptoms",
          routesTo: "direct",
          directSubtype: "LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY",
        },
      ],
    },
    subtypeQuestions: [
      {
        prompt: "When do you notice the pain the most?",
        options: [
          { id: "knee-sub-valgus", label: "With activities like squatting or going down stairs, and I notice my knee tends to dive inward.", value: "KNEE_VALGUS_COLLAPSE" },
          { id: "knee-sub-maltrack", label: "With prolonged sitting, and I feel a grinding or clicking under the kneecap.", value: "PATELLOFEMORAL_PAIN_WITH_MALTRACKING" },
          { id: "knee-sub-tendon", label: "A sharp pain right below my kneecap, especially with jumping or running.", value: "QUADRICEPS_LOADING_IMPACT_INTOLERANCE" },
        ],
      },
      {
        prompt: "Which best describes your knee symptoms?",
        options: [
          { id: "knee-sub-stiff", label: "My knee feels stiff, and I can't fully bend or straighten it.", value: "LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY" },
          { id: "knee-sub-unstable", label: "My knee feels like it might buckle, shift, or give way.", value: "TIBIAL_TRANSLATION_INSTABILITY" },
        ],
      },
    ],
  },
};


// ════════════════════════════════════════════════════════════════
// PAIN LOCATION COORDINATES (% of image: [x, y, width, height])
// Left-side defined; right side auto-mirrored.
// ════════════════════════════════════════════════════════════════

export const ZONE_COORDS = {
  LBP: {
    left: {
      'Lower Back': [36.5, 33.5, 12.0, 8.0],
      'Buttock': [36.1, 45.5, 12.8, 7.8],
      'Thigh': [36.8, 57.0, 11.4, 12.0],
      'Lower Leg': [37.5, 74.5, 10.0, 14.0],
    }
  },
  HIP: {
    left: {
      'Lower Back': [36.8, 38.0, 12.0, 6.2],
      'Buttock': [37.2, 46.8, 12.7, 3.8],
      'Front/Groin': [37.2, 51.1, 11.3, 4.0],
      'Side Hip': [31.0, 46.8, 5.5, 6.5],
      'Thigh': [37.2, 58.8, 10.5, 10.0],
      'Knee': [37.5, 69.5, 10.0, 5.0],
    }
  },
  THIGH: {
    left: {
      'Back': [35.8, 36.5, 13.3, 6.5],
      'Buttock': [36.5, 47.0, 12.0, 7.0],
      'Hip': [31.0, 45.0, 5.0, 9.0],
      'Back Thigh': [37.2, 56.0, 10.5, 5.0],
      'Front Thigh': [37.2, 61.0, 10.5, 5.0],
      'Inner Thigh': [47.7, 56.0, 4.2, 10.0],
      'Outer Thigh': [31.6, 56.0, 4.2, 10.0],
      'Knee': [37.5, 69.5, 10.0, 5.0],
    }
  },
  KNEE: {
    left: {
      'Back': [36.7, 36.5, 11.6, 6.5],
      'Buttock': [36.5, 47.0, 12.0, 7.0],
      'Hip': [31.0, 45.0, 5.0, 9.0],
      'Thigh': [37.2, 57.0, 10.5, 11.0],
      'Front Knee': [37.5, 69.5, 10.0, 2.5],
      'Back Knee': [37.5, 72.0, 10.0, 2.5],
      'Inner Knee': [47.5, 69.5, 3.5, 5.0],
      'Outer Knee': [32.5, 69.5, 3.5, 5.0],
    }
  },
};

// Build mirrored right-side coordinates for each body area
export function mirrorCoords(leftCoords) {
  const right = {};
  Object.entries(leftCoords).forEach(([k, v]) => {
    right[k] = [Math.round((100 - v[0] - v[2]) * 100) / 100, v[1], v[2], v[3]];
  });
  return right;
}

// Initialize right-side coordinates
Object.keys(ZONE_COORDS).forEach(area => {
  ZONE_COORDS[area].right = mirrorCoords(ZONE_COORDS[area].left);
});

// ── Zone Highlight Classification ──
export const FULL_ZONES = ['Lower Back', 'Buttock', 'Thigh', 'Lower Leg', 'Knee', 'Back'];
export const SPLIT_ZONES = ['Front/Groin', 'Back Thigh', 'Front Thigh', 'Front Knee', 'Back Knee'];
export const EDGE_ZONES = ['Side Hip', 'Hip', 'Inner Thigh', 'Outer Thigh', 'Inner Knee', 'Outer Knee'];

export function hlType(z) {
  return EDGE_ZONES.includes(z) ? 'edge' : SPLIT_ZONES.includes(z) ? 'split' : 'full';
}
export function splitHalf(z) {
  return z.startsWith('Back') ? 'upper' : 'lower';
}
export function edgeSide(z) {
  if (z === 'Side Hip' || z === 'Hip' || z.startsWith('Outer')) return 'outer';
  return 'inner';
}


// ════════════════════════════════════════════════════════════════
// SAFETY SCREEN — All Body Areas
// LBP: 3-path model (STOP, FUNCTIONAL_GATE with follow-ups)
// HKT: simple referral model (any checked = referral/stop)
// ════════════════════════════════════════════════════════════════

export const SAFETY_QUESTIONS = {
  LBP: {
    prompt: "Do any of the following apply to you?",
    conditions: [
      { id: "lbp-safety-bowel-bladder", label: "Changes in bowel or bladder control" },
      { id: "lbp-safety-shooting-leg", label: "Sharp or shooting pain down your legs" },
      { id: "lbp-safety-numbness", label: "Numbness or tingling in your legs or feet" },
      { id: "lbp-safety-weakness", label: 'Leg weakness, instability, or "giving out"' },
    ],
  },
  HIP: {
    prompt: "Do any of the following apply to you?",
    conditions: [
      { id: "hip-safety-bowel-bladder", label: "I have noticed changes in my bowel or bladder control" },
      { id: "hip-safety-shooting-numbness", label: "I have shooting leg pain, numbness, or tingling" },
      { id: "hip-safety-childhood", label: "I was diagnosed with a childhood hip condition (such as Perthes disease or a slipped hip)" },
      { id: "hip-safety-avn", label: "I have been told I have avascular necrosis or bone death in my hip" },
      { id: "hip-safety-replacement", label: "I have had a hip replacement or major hip surgery in the past 6 months" },
      { id: "hip-safety-trauma", label: "My hip pain started after a fall or significant injury" },
    ],
  },
  THIGH: {
    prompt: "Do any of the following apply to you?",
    conditions: [
      { id: "thigh-safety-bowel-bladder", label: "I have noticed changes in my bowel or bladder control" },
      { id: "thigh-safety-shooting-numbness", label: "I have shooting leg pain, numbness, or tingling" },
      { id: "thigh-safety-trauma", label: "My thigh pain started after a fall, direct hit, or other injury" },
      { id: "thigh-safety-surgery", label: "I have had leg or thigh surgery in the past 3 months" },
    ],
  },
  KNEE: {
    prompt: "Do any of the following apply to you?",
    conditions: [
      { id: "knee-safety-bowel-bladder", label: "I have noticed changes in my bowel or bladder control" },
      { id: "knee-safety-numbness", label: "I have numbness or tingling in my legs or feet" },
      { id: "knee-safety-weakness", label: "I have leg weakness, instability, or my leg gives out" },
      { id: "knee-safety-surgery-recent", label: "I have had knee surgery in the past 3 months" },
      { id: "knee-safety-trauma", label: "My knee pain started after a fall, twist, or direct injury" },
      { id: "knee-safety-ligament", label: "I have been told I have a torn ligament (ACL, PCL, MCL) that has not been treated" },
    ],
  },
};

// ════════════════════════════════════════════════════════════════
// LBP-SPECIFIC CPG & SUBTYPE LOGIC
// LBP uses a unique 2-step branching flow unlike HKT
// ════════════════════════════════════════════════════════════════

export const CPG_OPTIONS_LEG = [
  { id: "cpg-radiating", label: "A sharp, shooting pain that feels like a narrow line.", value: "LBP with Radiating Pain (Radiculopathy)" },
  { id: "cpg-referred", label: "A more spread out, dull, or deep ache.", value: "LBP with Related (Referred) Lower Extremity Pain" },
  { id: "cpg-unsure-leg", label: "Both / Not Sure", value: "LBP with Related (Referred) Lower Extremity Pain" },
];

export const CPG_OPTIONS_BACK = [
  { id: "cpg-mobility", label: "A feeling of deep, stiff aching and restricted movement.", value: "LBP with Mobility Deficits" },
  { id: "cpg-coordination", label: 'A feeling of instability, weakness, or the back "giving out".', value: "LBP with Movement Coordination Impairments" },
  { id: "cpg-unsure-back", label: "Both / Not Sure", value: "LBP with Movement Coordination Impairments" },
];

export const Q1_OPTIONS = [
  { id: "q1a", value: "a", label: "Feels worse when sitting, slouching, or bending forward." },
  { id: "q1b", value: "b", label: "Feels worse when standing or walking for a long time." },
  { id: "q1e", value: "e", label: "Feels worse with twisting, turning, or getting in/out of a car." },
  { id: "q1c", value: "c", label: "Feels worse if I stay in one position too long." },
  { id: "q1d", value: "d", label: "Hard to say — it seems to be unpredictable." },
];

export const Q2_MAP = {
  a: { title: "Does gently arching your back or standing up straight help ease the pain?", name: "q2a" },
  b: { title: "Does sitting down or slouching help ease the pain?", name: "q2b" },
  e: { title: "Does it feel better when you avoid twisting motions?", name: "q2e" },
  c: { title: "Is changing positions the best way to find relief?", name: "q3" },
  d: { title: "Is changing positions the best way to find relief?", name: "q3" },
};

export function getSubtype(q1, q2) {
  if (q1 === "a") return q2 === "yes" ? "FLEXION_INTOLERANT" : "NEUTRAL_SENSITIVE";
  if (q1 === "b") return q2 === "yes" ? "EXTENSION_INTOLERANT" : "NEUTRAL_SENSITIVE";
  if (q1 === "e") return q2 === "yes" ? "ROTATIONAL_INTOLERANT" : "NEUTRAL_SENSITIVE";
  return "NEUTRAL_SENSITIVE";
}

export function cpgToSubtype(cpgValue) {
  if (!cpgValue) return null;
  if (cpgValue.includes("Mobility Deficits")) return "LIMITED_LUMBAR_MOBILITY";
  if (cpgValue.includes("Movement Coordination")) return "NEUTRAL_SENSITIVE";
  return null;
}

export const LEG_ZONES = ["left:Thigh", "right:Thigh", "left:Lower Leg", "right:Lower Leg"];

export function hasLegPain(zones) {
  return zones.some(z => LEG_ZONES.includes(z));
}


// ════════════════════════════════════════════════════════════════
// MOVEMENT TEST MAPS — All Body Areas
// Flat lookup by subtype. Shared subtypes use the same test.
// ════════════════════════════════════════════════════════════════

export const TEST_MAP = {
  // LBP
  FLEXION_INTOLERANT: { name: "Standing Lumbar Extension", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/StandingLumbarExtensionROMv1.0.mp4" },
  EXTENSION_INTOLERANT: { name: "Seated Single Knee to Chest", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/SeatedSingleKneeToChestv1.0.mp4" },
  NEUTRAL_SENSITIVE: { name: "Seated Trunk Rotation", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/SeatedTrunkRotationv1.0.mp4" },
  LIMITED_LUMBAR_MOBILITY: { name: "Side to Side Bend", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/SideToSideBendv1.0.mp4" },
  ROTATIONAL_INTOLERANT: { name: "Standing Lumbar Extension", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/StandingLumbarExtensionROMv1.0.mp4" },
  // HIP
  ANTERIOR_CHAIN_MOBILITY_DEFICIT: { name: "Standing Hip Flexor Stretch", video: PLACEHOLDER_VIDEO },
  POSTERIOR_CHAIN_MOBILITY_DEFICIT: { name: "Seated Glute Stretch", video: PLACEHOLDER_VIDEO },
  NEUROMUSCULAR_MOBILITY_DEFICIT: { name: "Seated Glute Stretch", video: PLACEHOLDER_VIDEO },
  ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: { name: "Seated Hip Hinge", video: PLACEHOLDER_VIDEO },
  HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: { name: "Seated Hip Abduction", video: PLACEHOLDER_VIDEO },
  PELVIC_CONTROL_IMPAIRMENT: { name: "Seated Hip Hinge", video: PLACEHOLDER_VIDEO },
  // THIGH
  QUADRICEPS_LOADING_IMPACT_INTOLERANCE: { name: "Seated Isometric Knee Extension", video: PLACEHOLDER_VIDEO },
  HAMSTRING_LOADING_LENGTHENING_DEFICIT: { name: "Toe Touch", video: PLACEHOLDER_VIDEO },
  ITB_FRICTION_SYNDROME: { name: "Standing ITB Stretch - Side Bend", video: PLACEHOLDER_VIDEO },
  // KNEE (subtypes not already defined above)
  KNEE_VALGUS_COLLAPSE: { name: "Seated Hip Abduction", video: PLACEHOLDER_VIDEO },
  PATELLOFEMORAL_PAIN_WITH_MALTRACKING: { name: "Seated Isometric Knee Extension", video: PLACEHOLDER_VIDEO },
  LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: { name: "Seated Isometric Knee Extension", video: PLACEHOLDER_VIDEO },
  TIBIAL_TRANSLATION_INSTABILITY: { name: "Seated Isometric Knee Extension", video: PLACEHOLDER_VIDEO },
};

export const NEGATIVE_TEST_MAP = {
  // LBP
  FLEXION_INTOLERANT: { name: "Toe Touch", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/ToeTouchNegativeTestv1.0.mp4" },
  EXTENSION_INTOLERANT: { name: "Standing Lumbar Extension", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/StandingLumbarExtensionROM-Negativev1.0.mp4" },
  LIMITED_LUMBAR_MOBILITY: { name: "Seated Trunk Rotation", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/SeatedTrunkRotationvNegative1.0.mp4" },
  ROTATIONAL_INTOLERANT: { name: "Seated Trunk Rotation", video: "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/SeatedTrunkRotationv1.0.mp4" },
  NEUTRAL_SENSITIVE: null,
  // HIP
  ANTERIOR_CHAIN_MOBILITY_DEFICIT: { name: "Standing Hip Extension", video: PLACEHOLDER_VIDEO },
  POSTERIOR_CHAIN_MOBILITY_DEFICIT: { name: "Seated Single Knee to Chest", video: PLACEHOLDER_VIDEO },
  NEUROMUSCULAR_MOBILITY_DEFICIT: { name: "Toe Touch", video: PLACEHOLDER_VIDEO },
  ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: { name: "Lateral Lunge", video: PLACEHOLDER_VIDEO },
  HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: { name: "Standing Hip Abduction", video: PLACEHOLDER_VIDEO },
  PELVIC_CONTROL_IMPAIRMENT: { name: "Squat", video: PLACEHOLDER_VIDEO },
  // THIGH
  QUADRICEPS_LOADING_IMPACT_INTOLERANCE: { name: "Sit to Stand", video: PLACEHOLDER_VIDEO },
  HAMSTRING_LOADING_LENGTHENING_DEFICIT: { name: "Sit to Stand", video: PLACEHOLDER_VIDEO },
  ITB_FRICTION_SYNDROME: { name: "Standing ITB Stretch - Toe Touch", video: PLACEHOLDER_VIDEO },
  // KNEE
  KNEE_VALGUS_COLLAPSE: { name: "Squat", video: PLACEHOLDER_VIDEO },
  PATELLOFEMORAL_PAIN_WITH_MALTRACKING: { name: "Seated Squat", video: PLACEHOLDER_VIDEO },
  LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: { name: "Seated Squat", video: PLACEHOLDER_VIDEO },
  TIBIAL_TRANSLATION_INSTABILITY: { name: "Seated Squat", video: PLACEHOLDER_VIDEO },
};

export const KNEE_NEGATIVE_TEST_OVERRIDES = {
  ITB_FRICTION_SYNDROME: { name: "Squat", video: PLACEHOLDER_VIDEO },
};

export function getNegativeTest(subtype, problemArea) {
  if (problemArea === 'KNEE' && KNEE_NEGATIVE_TEST_OVERRIDES[subtype]) {
    return KNEE_NEGATIVE_TEST_OVERRIDES[subtype];
  }
  return NEGATIVE_TEST_MAP[subtype] || null;
}

export const TEST_INTRO_VIDEOS = {
  // LBP
  "Standing Lumbar Extension": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/IntroToAR-StandingLumbarExtension-v1.0.mp4",
  "Supine Double Knee to Chest": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/IntroToAR-SupineDoubleKneeToChest-v1.0.mp4",
  "Seated Trunk Rotation": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/IntroToAR-SeatedTrunkRotation-v1.0.mp4",
  "Side to Side Bend": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/IntroToAR-SideToSideBend-v1.0.mp4",
  // HKT
  "Seated Hip Abduction": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SeatedHipAbduction.mp4",
  "Squat": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-Squat-Neg.mp4",
  "Seated Squat": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SeatedSquat-Neg.mp4",
  "Sit to Stand": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SitToStand-Neg.mp4",
  "Seated Isometric Knee Extension": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SeatedIsometricKneeExtension.mp4",
  "Seated Hip Hinge": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SeatedHipHinge.mp4",
  "Standing Hip Abduction": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-StandingHipAbduction-Neg.mp4",
  "Standing Hip Extension": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-StandingHipExtension-Neg.mp4",
  "Standing Hip Flexor Stretch": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-StandingHipFlexorStretch.mp4",
  "Seated Glute Stretch": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SeatedGluteStretch.mp4",
  "Lateral Lunge": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-LateralLunge-Neg.mp4",
  "Standing ITB Stretch - Side Bend": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-StandingITBStretch-SideBend.mp4",
  "Standing ITB Stretch - Toe Touch": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-StandingITBStretch-ToeTouch-Neg.mp4",
  "Toe Touch": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/ToeTouchNegativeTestv1.0.mp4",
  "Seated Single Knee to Chest": "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_selfdemovideo/GSA-SeatedSingleKneeToChest-Neg.mp4",
};

export const SUBTYPE_TO_CPG_MAP = {
  HIP: {
    ANTERIOR_CHAIN_MOBILITY_DEFICIT: "Hip Pain with Mobility Deficits",
    POSTERIOR_CHAIN_MOBILITY_DEFICIT: "Hip Pain with Mobility Deficits",
    NEUROMUSCULAR_MOBILITY_DEFICIT: "Hip Pain with Mobility Deficits",
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "Hip Pain with Muscle Power Deficits",
    HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: "Hip Pain with Muscle Power Deficits",
    PELVIC_CONTROL_IMPAIRMENT: "Hip Pain with Impaired Motor Control",
  },
  THIGH: {
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "Thigh Pain with Muscular Origin",
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "Thigh Pain with Muscular Origin",
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: "Thigh Pain with Muscular Origin",
    ITB_FRICTION_SYNDROME: "Thigh Pain with Muscular Origin",
  },
  KNEE: {
    KNEE_VALGUS_COLLAPSE: "Patellofemoral Pain (PFP)",
    PATELLOFEMORAL_PAIN_WITH_MALTRACKING: "Patellofemoral Pain (PFP)",
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "Knee Pain with Tendon Loading Deficits",
    LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: "Knee Pain with Mobility/Stability Deficits",
    TIBIAL_TRANSLATION_INSTABILITY: "Knee Pain with Mobility/Stability Deficits",
    ITB_FRICTION_SYNDROME: "Knee Pain with Lateral Symptoms",
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: "Knee Pain with Posterior Symptoms",
  },
};


// ════════════════════════════════════════════════════════════════
// SELF-REPORT, RPE, FEEL AFFECT, FEEDBACK 2, DURATION
// Same for all body areas
// ════════════════════════════════════════════════════════════════

export const SELF_REPORT_ROM = {
  heading: "How Did Your Movements Compare?",
  subheading: "Think about the first few reps you did and the last few reps. How did your movements change?",
  options: [
    { id: "rom_smaller", title: "Got smaller", subtitle: "My movements became smaller as I kept going.", points: 0 },
    { id: "rom_bigger", title: "Got bigger", subtitle: "My movements became bigger as I kept going.", points: 5 },
    { id: "rom_same", title: "Stayed the same", subtitle: "My last few reps and the first few were the same.", points: 10 },
  ]
};

export const SELF_REPORT_TEMPO = {
  heading: "How Was Your Speed?",
  subheading: "Compared to Myra's count, how would you describe your speed?",
  options: [
    { id: "tempo_kept_up", title: "Kept up", subtitle: "I matched Myra's speed or moved slightly faster.", points: 10, outcome: "CONTINUE" },
    { id: "tempo_slower", title: "Slowed down", subtitle: "I finished all the reps but moved slower.", points: 5, outcome: "CONTINUE" },
    { id: "tempo_not_finished", title: "Couldn't finish", subtitle: "I couldn't finish all the reps and had to stop.", points: 0, outcome: "HARD_STOP" },
  ]
};

export const RPE_OPTIONS = [
  { value: 1, label: "Easy" },
  { value: 3, label: "Kinda Easy" },
  { value: 5, label: "Just Right" },
  { value: 7, label: "Kinda Hard" },
  { value: 9, label: "Hard" },
];

export const FEEL_AFFECT_OPTIONS = [
  { id: "symptom-improved", label: "Felt Better", value: "IMPROVED" },
  { id: "symptom-tolerable", label: "Felt Same", value: "TOLERABLE" },
  { id: "symptom-worsened", label: "Felt Worse", value: "WORSENED" },
];

export const FEEDBACK2_OPTIONS = [
  { id: "fb2-better", label: "Felt Better", value: "BETTER" },
  { id: "fb2-worse", label: "Felt Worse", value: "WORSE" },
];

export const DURATION_OPTIONS = [
  { id: "duration-acute", value: "ACUTE", label: "Less than 6 weeks" },
  { id: "duration-subacute", value: "SUBACUTE", label: "6 weeks to 3 months" },
  { id: "duration-chronic", value: "CHRONIC", label: "More than 3 months" },
];


// ════════════════════════════════════════════════════════════════
// RPE & SELF-REPORT VOICE RESPONSE FUNCTIONS
// Stubs — need production speech text from VoiceService.
// Chunk 4 (RPE/self-report screens) will use these.
// ════════════════════════════════════════════════════════════════

// TODO: Populate with production Myra speech text per RPE value.
export function getRpeResponseText(value) {
  const responses = {
    1: "That was easy for you — good to know.",
    3: "A little bit of effort — noted.",
    5: "Right in the middle — that's useful information.",
    7: "That was pretty challenging — I'll keep that in mind.",
    9: "That was tough — thanks for pushing through it.",
  };
  return responses[value] || "";
}

// TODO: Populate with production Myra speech text per self-report selection.
// type: 'rom' | 'tempo', value: the option id
export function getSelfReportReplyText(type, value) {
  const responses = {
    rom: {
      rom_smaller: "Your range of motion decreased — that's useful to know.",
      rom_bigger: "Your range of motion increased — that's a positive sign.",
      rom_same: "Consistent movement — that's good information.",
    },
    tempo: {
      tempo_kept_up: "You kept up with the pace — great.",
      tempo_slower: "You slowed down a bit — that's completely okay.",
      tempo_not_finished: "You weren't able to finish — that's important for me to know.",
    },
  };
  return responses[type]?.[value] || "";
}


// ════════════════════════════════════════════════════════════════
// PERSONALIZE (Optional — grouped cards)
// ════════════════════════════════════════════════════════════════

export const PERSONALIZE_GROUPS = [
  {
    id: "basics",
    myraIntro: "To get your plan right, I just need a few details about you. Let's start with the basics.",
    fields: [
      { id: "age", type: "number", label: "Age", placeholder: "Your age", min: 18, max: 99 },
      { id: "weight", type: "number", label: "Weight (lbs)", placeholder: "Your weight", min: 50, max: 500 },
      { id: "heightFt", type: "number", label: "Height (ft)", placeholder: "Feet", min: 3, max: 8 },
      { id: "heightIn", type: "number", label: "Height (in)", placeholder: "Inches", min: 0, max: 11 },
    ]
  },
  {
    id: "capacity",
    myraIntro: "A couple more — these help me set the right intensity.",
    fields: [
      {
        id: "activity", type: "options", label: "How many days per week are you typically active?",
        options: [
          { id: "activity-sedentary", value: "SEDENTARY", label: "0" },
          { id: "activity-light", value: "LIGHTLY_ACTIVE", label: "1-2" },
          { id: "activity-active", value: "ACTIVE", label: "3-4" },
          { id: "activity-very-active", value: "VERY_ACTIVE", label: "5+" },
        ]
      },
      {
        id: "floorCapability", type: "options", label: "Are you comfortable getting down to the floor and back up?",
        options: [
          { id: "capability-floor-yes", value: "yes", label: "Yes, I am comfortable" },
          { id: "capability-floor-no", value: "no", label: "No, it is too difficult right now" },
        ]
      },
      {
        id: "balance", type: "options", label: "How would you describe your balance?",
        options: [
          { id: "balance-steady", value: "steady", label: "Steady" },
          { id: "balance-mixed", value: "unsteady", label: "Steady & Unsteady" },
        ]
      },
    ]
  },
  {
    id: "preferences",
    myraIntro: "Last section — your goals and schedule preferences.",
    fields: [
      {
        id: "goals", type: "goals", label: "What are your goals?",
        options: [
          { id: "goal-reduce-pain", value: "reduce_pain", label: "Reduce Pain" },
          { id: "goal-less-stiff", value: "less_stiff", label: "Reduce Stiffness" },
          { id: "goal-improve-strength", value: "improve_strength", label: "Improve Strength" },
          { id: "goal-improve-flexibility", value: "improve_flexibility", label: "Improve Flexibility" },
          { id: "goal-daily-tasks", value: "daily_tasks", label: "Make Daily Tasks Easier" },
          { id: "goal-balance", value: "balance", label: "Improve Balance & Stability" },
          { id: "goal-picking-up", value: "picking_up", label: "Picking Up Things" },
          { id: "goal-sleep", value: "sleep", label: "Improve Sleep Quality" },
          { id: "goal-walking", value: "walking", label: "Improve Walking & Endurance" },
          { id: "goal-lifting", value: "lifting", label: "Make Bending & Lifting Easier" },
          { id: "goal-posture", value: "posture", label: "Improve Sitting & Standing Posture" },
          { id: "goal-rotational-sports", value: "rotational_sports", label: "Return to Sports (Golf, Tennis, etc.)" },
          { id: "goal-overhead-sports", value: "overhead_sports", label: "Return to Sports (Basketball, Swimming, etc.)" },
          { id: "goal-running", value: "running", label: "Running, Cycling, etc." },
          { id: "goal-fitness", value: "fitness", label: "Fitness: Gym, CrossFit, etc." },
        ]
      },
      {
        id: "scheduleDays", type: "options", label: "How many days per week can you commit?",
        options: [
          { id: "days-2", value: "2", label: "2" },
          { id: "days-3", value: "3", label: "3" },
          { id: "days-4", value: "4", label: "4" },
          { id: "days-5", value: "5", label: "5" },
          { id: "days-6", value: "6", label: "6" },
          { id: "days-7", value: "7", label: "7" },
        ]
      },
      {
        id: "scheduleTime", type: "options", label: "How long per session?",
        options: [
          { id: "time-10", value: "10", label: "10 min" },
          { id: "time-15", value: "15", label: "15 min" },
          { id: "time-20", value: "20", label: "20 min" },
        ]
      },
      {
        id: "sessionPref", type: "options", label: "Sessions per day",
        options: [
          { id: "session-one", value: "one", label: "One Session" },
          { id: "session-multiple", value: "multiple", label: "Two Sessions" },
        ]
      },
    ]
  },
];

export const PERSONALIZE_SKIP_MESSAGES = {
  basics: "I'll use conservative settings for your plan. Providing your age, weight, and height later will help me make it more accurate.",
  capacity: "Without knowing your activity level and floor capability, I'll start you at a gentler intensity to be safe.",
  preferences: "I'll set you up with a standard schedule — you can adjust this anytime.",
};


// ════════════════════════════════════════════════════════════════
// STEP LABELS & SUBTYPE DISPLAY NAMES
// ════════════════════════════════════════════════════════════════

export const STEP_LABELS = {
  intro: "Getting Started",
  problem_area: "Problem Area",
  pain_location: "Pinpoint Your Pain",
  safety: "Safety Check",
  safety_followup: "Safety Check",
  cpg: "Pain Character",
  subtype_q1: "Pain Pattern",
  q2: "Pain Pattern",
  pre_nprs: "Pain Level",
  demo_video_1: "Movement Test 1",
  demo_video_2: "Movement Test 2",
  camera: "Camera Setup",
  movement_1: "Movement Test 1",
  movement_2: "Movement Test 2",
  self_report_movement_1: "Movement Test 1",
  self_report_movement_2: "Movement Test 2",
  self_report_rom: "Self-Report",
  self_report_tempo: "Self-Report",
  rpe: "How Challenging",
  feel_affect: "Symptom Response",
  feedback2: "Movement Comparison",
  personalize: "Personalize Your Plan",
  recovery_score: "Recovery Compass",
  plan: "Your Recovery Plan",
  hard_stop: "Safety Recommendation",
};

export const SUBTYPE_DISPLAY_NAMES = {
  // LBP
  FLEXION_INTOLERANT: "Flexion-Sensitive",
  EXTENSION_INTOLERANT: "Extension-Sensitive",
  NEUTRAL_SENSITIVE: "Neutral Zone Sensitivity",
  LIMITED_LUMBAR_MOBILITY: "Limited Lumbar Mobility",
  ROTATIONAL_INTOLERANT: "Rotation-Sensitive",
  // HIP
  ANTERIOR_CHAIN_MOBILITY_DEFICIT: "Anterior Chain Mobility Deficit",
  POSTERIOR_CHAIN_MOBILITY_DEFICIT: "Posterior Chain Mobility Deficit",
  NEUROMUSCULAR_MOBILITY_DEFICIT: "Neuromuscular Mobility Deficit",
  ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "Adductor Overload/Control Deficit",
  HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: "Hip Abduction/External Rotation Control Deficit",
  PELVIC_CONTROL_IMPAIRMENT: "Pelvic Control Impairment",
  // THIGH
  QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "Quadriceps Loading/Impact Intolerance",
  HAMSTRING_LOADING_LENGTHENING_DEFICIT: "Hamstring Loading/Lengthening Deficit",
  ITB_FRICTION_SYNDROME: "ITB Friction Syndrome",
  // KNEE
  KNEE_VALGUS_COLLAPSE: "Knee Valgus Collapse",
  PATELLOFEMORAL_PAIN_WITH_MALTRACKING: "Patellofemoral Pain with Maltracking",
  LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: "Limited Knee Extension/Flexion Mobility",
  TIBIAL_TRANSLATION_INSTABILITY: "Tibial Translation (Instability)",
};


// ════════════════════════════════════════════════════════════════
// MESSAGING — Diagnosis, Plan Focus, Readiness Tiers
// ════════════════════════════════════════════════════════════════

export const MESSAGING = {
  diagnosis: {
    // LBP
    FLEXION_INTOLERANT: {
      headline: "Your Back Dislikes Bending Forward",
      clinicalTerm: "Flexion Intolerance",
      implications: "Activities that involve rounding your lower back, like slouching or tying your shoes, are likely to increase your discomfort.",
      relief: "You May Find That Standing Or Arching Your Back Provides Relief",
    },
    EXTENSION_INTOLERANT: {
      headline: "Your Back Dislikes Arching or Extended Standing",
      clinicalTerm: "Extension Intolerance",
      implications: "You might find that prolonged standing or walking is uncomfortable, and that sitting or slouching provides relief.",
      relief: "You May Find That Sitting Or Slouching Provides Relief",
    },
    LIMITED_LUMBAR_MOBILITY: {
      headline: "Your Back Pain May Be Caused by Stiffness",
      clinicalTerm: "Limited Lumbar Mobility",
      implications: "Because your lower back isn't moving as freely as it could, you might find that activities like putting on your shoes or getting out of a car feel stiff and difficult.",
      relief: "Gentle Movement In All Directions May Provide Relief",
    },
    NEUTRAL_SENSITIVE: {
      headline: "Your Back Seems to Want More Support",
      clinicalTerm: "Movement Coordination Impairments",
      implications: 'You might notice that your back feels weak, unstable, or like it might "give out."',
      relief: "Changing Positions Frequently May Be Your Best Source Of Relief Right Now",
    },
    ROTATIONAL_INTOLERANT: {
      headline: "Your Back Dislikes Twisting",
      clinicalTerm: "Rotational Intolerance",
      implications: "You might find that movements involving twisting, turning, or reaching across your body increase your discomfort.",
      relief: "Minimizing Twisting Motions May Provide Relief",
    },
    // HIGH_FUNCTIONING: triggered when backend returns complexityLevel === 5
    // Cannot be determined client-side — requires Recovery Compass Score API.
    HIGH_FUNCTIONING: {
      headline: "Your Back Is Ready to Level Up",
      clinicalTerm: "High Functioning / Low Irritability",
      implications: "You've shown strong control and minimal symptoms during the assessment, so you are well-positioned to progress beyond basic exercises and build a stronger, more durable back.",
      relief: "You May Find That Standing Or Arching Your Back Provides Relief",
    },
    // HIP
    ANTERIOR_CHAIN_MOBILITY_DEFICIT: {
      headline: "Your Hip Pain May Be Related to Tight Hip Flexors",
      clinicalTerm: "Anterior Chain Mobility Deficit",
      implications: "You might find that extending your leg behind you, standing up after prolonged sitting, or walking uphill increases your discomfort.",
      relief: "Hip Flexor Stretching May Provide Relief",
    },
    POSTERIOR_CHAIN_MOBILITY_DEFICIT: {
      headline: "Your Hip Pain May Be Related to Tight Posterior Muscles",
      clinicalTerm: "Posterior Chain Mobility Deficit",
      implications: "You might find that bringing your knee toward your chest, sitting for long periods, or certain rotational movements increase your discomfort.",
      relief: "Glute and Hip Mobility Work May Provide Relief",
    },
    NEUROMUSCULAR_MOBILITY_DEFICIT: {
      headline: "Your Hip Pain May Have a Neural Component",
      clinicalTerm: "Neuromuscular Mobility Deficit",
      implications: "You might notice a deep, specific pain in your buttock that sometimes travels down your leg, especially with certain sitting positions.",
      relief: "Gentle Neural Mobilization May Provide Relief",
    },
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: {
      headline: "Your Pain May Be Related to Inner Thigh Muscles",
      clinicalTerm: "Adductor Overload/Control Deficit",
      implications: "You might find that moving your legs apart, side-stepping, or activities that stress your inner thigh increase your discomfort.",
      relief: "Inner Thigh Strengthening May Provide Relief",
    },
    HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: {
      headline: "Your Hip May Need Better Side Support",
      clinicalTerm: "Hip Abduction/External Rotation Control Deficit",
      implications: "You might notice your hip drops when walking, or you feel unsteady when standing on one leg or climbing stairs.",
      relief: "Hip Abductor Strengthening May Provide Relief",
    },
    PELVIC_CONTROL_IMPAIRMENT: {
      headline: "Your Hip Needs Better Pelvic Control",
      clinicalTerm: "Pelvic Control Impairment",
      implications: "You might notice a pinching, clicking, or catching feeling in the front of your hip during certain movements, or feel like your pelvis isn't stable.",
      relief: "Pelvic Stability Exercises May Provide Relief",
    },
    // THIGH
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: {
      headline: "Your Thigh Pain May Be Related to Quadriceps Loading",
      clinicalTerm: "Quadriceps Loading/Impact Intolerance",
      implications: "You might find that activities like running, jumping, going down stairs, or kicking increase your front thigh discomfort.",
      relief: "Progressive Quadriceps Loading May Provide Relief",
    },
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: {
      headline: "Your Thigh Pain May Be Related to Hamstring or Posterior Chain",
      clinicalTerm: "Hamstring Loading/Lengthening Deficit",
      implications: "You might find that bending forward, stretching, quick movements, or prolonged sitting increase your thigh discomfort.",
      relief: "Progressive Hamstring Work May Provide Relief",
    },
    ITB_FRICTION_SYNDROME: {
      headline: "Your Pain May Be Related to IT Band Tightness",
      clinicalTerm: "ITB Friction Syndrome",
      implications: "You might notice pain on the outer side of your thigh or knee, especially with repetitive activities like running or cycling.",
      relief: "IT Band Stretching and Hip Strengthening May Provide Relief",
    },
    // KNEE
    KNEE_VALGUS_COLLAPSE: {
      headline: "Your Knee Pain May Be Related to Inward Knee Motion",
      clinicalTerm: "Knee Valgus Collapse",
      implications: "You might notice your knee diving inward during squats, stairs, or landing from jumps.",
      relief: "Hip Strengthening to Control Knee Position May Provide Relief",
    },
    PATELLOFEMORAL_PAIN_WITH_MALTRACKING: {
      headline: "Your Knee Pain May Be Related to Kneecap Tracking",
      clinicalTerm: "Patellofemoral Pain with Maltracking",
      implications: "You might notice grinding, clicking, or pain under your kneecap, especially after prolonged sitting.",
      relief: "Quadriceps Control and Patellar Tracking Exercises May Provide Relief",
    },
    LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: {
      headline: "Your Knee May Need Better Mobility",
      clinicalTerm: "Limited Knee Extension/Flexion Mobility",
      implications: "You might find it difficult to fully straighten or bend your knee, with stiffness throughout the range.",
      relief: "Gentle Range of Motion Exercises May Provide Relief",
    },
    TIBIAL_TRANSLATION_INSTABILITY: {
      headline: "Your Knee May Feel Unstable",
      clinicalTerm: "Tibial Translation (Instability)",
      implications: "You might feel like your knee could buckle, shift, or give way during activities.",
      relief: "Controlled Strengthening to Improve Stability May Provide Relief",
    },
  },
  readinessTiers: {
    READY_TO_GO: {
      headline: "Ready to Go",
      description: "Your history and health factors show you're ready for a standard starting intensity.",
    },
    CAUTIOUS_START: {
      headline: "A Cautious Start",
      description: "We've slightly adjusted the intensity of your plan to ensure your body adapts well as you get started.",
    },
    GENTLE_APPROACH: {
      headline: "A Gentle Approach",
      description: "We've adjusted your plan to begin with a gentler intensity, giving your body extra time to respond and build momentum safely.",
    },
    STARTING_WITH_CARE: {
      headline: "Starting with Care",
      description: "To ensure the safest start, your plan begins with a very gentle intensity, focused on building a solid foundation.",
    },
  },

  planFocus: {
    // LBP
    FLEXION_INTOLERANT: "Reduce Sensitivity to Bending",
    EXTENSION_INTOLERANT: "Relieve Pressure from Arching",
    LIMITED_LUMBAR_MOBILITY: "Improve Flexibility",
    NEUTRAL_SENSITIVE: "Build Core Support",
    ROTATIONAL_INTOLERANT: "Reduce Sensitivity to Twisting",
    HIGH_FUNCTIONING: "Build Strength & Durability",
    // HIP
    ANTERIOR_CHAIN_MOBILITY_DEFICIT: "Improve Hip Flexor Flexibility",
    POSTERIOR_CHAIN_MOBILITY_DEFICIT: "Improve Hip Mobility",
    NEUROMUSCULAR_MOBILITY_DEFICIT: "Address Neural Tension",
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "Strengthen Inner Thigh Control",
    HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: "Strengthen Hip Abductors",
    PELVIC_CONTROL_IMPAIRMENT: "Improve Pelvic Stability",
    // THIGH
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "Build Quadriceps Tolerance",
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: "Improve Posterior Chain Flexibility",
    ITB_FRICTION_SYNDROME: "Release IT Band Tension",
    // KNEE
    KNEE_VALGUS_COLLAPSE: "Control Knee Alignment",
    PATELLOFEMORAL_PAIN_WITH_MALTRACKING: "Improve Kneecap Tracking",
    LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: "Restore Knee Range of Motion",
    TIBIAL_TRANSLATION_INSTABILITY: "Build Knee Stability",
  },
};

export const READINESS_TIERS = {
  READY_TO_GO: { headline: "Ready to Go", description: "Your history and health factors show you're ready for a standard starting intensity." },
  CAUTIOUS_START: { headline: "A Cautious Start", description: "We've slightly adjusted the intensity of your plan to ensure your body adapts well." },
  GENTLE_APPROACH: { headline: "A Gentle Approach", description: "We've adjusted your plan to begin with a gentler intensity, giving your body extra time to respond." },
  STARTING_WITH_CARE: { headline: "Starting with Care", description: "Your plan begins with a very gentle intensity, focused on building a solid foundation." },
};


// ════════════════════════════════════════════════════════════════
// EXIT HANDLER CONFIG & MESSAGES
// ════════════════════════════════════════════════════════════════

export const EXIT_CONFIGS = {
  gsa: {
    heading: "Having Trouble or Thinking About Leaving?",
    subheading: "Let us know if you need help or want to exit — we're here to support you.",
    tiles: [
      { reason: 'confused', title: "I'm not sure how to answer", desc: "Not sure what fits your situation? Myra can help with any questions you have." },
      { reason: 'technical', title: "Are you having technical difficulties?", desc: "If something isn't working, Myra can help troubleshoot the issue." },
      { reason: 'leave', title: "I want to leave the assessment", desc: "If you decide to leave, we'd love to hear how we can improve." },
    ]
  },
  personalize: {
    heading: "We Can Help or Want to Exit?",
    subheading: "This information helps us create your personalized recovery plan.",
    tiles: [
      { reason: 'why_info', title: "Why do you need this information?", desc: "Curious why we're asking for age, weight, or other details? Myra can explain everything." },
      { reason: 'confused', title: "I'm not sure how to answer", desc: "Not sure what to put? Myra can guide you through any question or field." },
      { reason: 'technical', title: "Something isn't working", desc: "If the screen or buttons aren't responding, Myra can help troubleshoot the issue." },
      { reason: 'leave', title: "I want to leave the assessment", desc: "If you decide to stop, we'd love to hear how we can improve your experience." },
    ]
  },
  movement_test: {
    heading: "Tracking Trouble or Want to Exit?",
    subheading: "Let us know if you need help or want to exit — we're here to support you.",
    tiles: [
      { reason: 'tracking', title: "Tracking isn't working correctly", desc: "Having trouble with the camera or body tracking? Myra can help troubleshoot." },
      { reason: 'confused', title: "I'm not sure how to answer", desc: "Not sure what fits your situation? Myra can help with any questions you have." },
      { reason: 'technical', title: "Something isn't working", desc: "If the screen or buttons aren't responding, Myra can help troubleshoot the issue." },
      { reason: 'leave', title: "I want to leave the assessment", desc: "If you decide to stop, we'd love to hear how we can improve your experience." },
    ]
  }
};

export const EXIT_MYRA_MESSAGES = {
  confused: "No worries — what part is unclear? I'm happy to walk you through it.",
  technical: "Sorry about that. Can you tell me what's happening — is the screen frozen, or is something not responding?",
  why_info: "Great question. Your age, weight, and other details help me choose exercises at the right intensity and make sure they're safe. What specifically are you wondering about?",
  tracking: "I see you're having trouble with the tracking. Are the targets not showing up, or is the camera not picking up your body?",
};

export function getExitContext(step) {
  const s = step || '';
  if (s.includes('personalize')) return 'personalize';
  if (s.includes('movement') || s.includes('self_report') || s.includes('demo_video') || s.includes('camera')) return 'movement_test';
  return 'gsa';
}


// ════════════════════════════════════════════════════════════════
// INTENT DETECTION — Intro screen pre-selection
// ════════════════════════════════════════════════════════════════

export const AREA_MAP = { 'lower back': 'LBP', 'back': 'LBP', 'hip': 'HIP', 'thigh': 'THIGH', 'knee': 'KNEE', 'leg': 'KNEE' };

export function detectArea(text) {
  const lower = text.toLowerCase();
  for (const [kw, id] of Object.entries(AREA_MAP)) {
    if (lower.includes(kw)) return id;
  }
  return null;
}

export const TRIGGER_KEYWORDS = {
  a: ['bend', 'sit', 'slouch', 'forward', 'sitting'],
  b: ['stand', 'walk', 'standing', 'walking'],
  e: ['twist', 'turn', 'rotation', 'car'],
  c: ['position', 'still', 'one position', 'stay'],
};

export const DURATION_KEYWORDS = {
  ACUTE: ['few days', 'last week', 'just started', 'recent', 'few weeks'],
  CHRONIC: ['months', 'years', 'long time', 'forever', 'chronic'],
};


// ════════════════════════════════════════════════════════════════
// PLAN Q&A ANSWERS — canned responses for plan screen quick-ask
// ════════════════════════════════════════════════════════════════

export const PLAN_QA_ANSWERS = {
  why: "Each exercise was chosen based on your pain pattern, movement results, and subtype classification. They target the specific areas that need strengthening or mobility work for your recovery.",
  often: "Your plan is designed for 3 sessions per week. Each session takes about 15–20 minutes. Consistency matters more than intensity — even 2 sessions is better than none.",
  hurts: "Mild discomfort during exercises can be normal, but sharp or increasing pain is not. If an exercise causes pain above a 4 out of 10 or gets worse as you go, stop and let me know. I can swap it for an alternative.",
};


// ════════════════════════════════════════════════════════════════
// DUMMY RECOVERY PLANS — Per Body Area (prototype/testing only)
// Production gets plans from the backend.
// ════════════════════════════════════════════════════════════════

export const DUMMY_PLANS = {
  LBP: {
    score: 42,
    readinessModifier: 85,
    estimatedTime: "15-20",
    exercises: [
      { name: "Cat-Cow Stretch", sets: 2, reps: 10, hold: null, rationale: "Gentle spinal mobility helps reduce stiffness and promote fluid movement through your lower back." },
      { name: "Pelvic Tilts", sets: 2, reps: 12, hold: null, rationale: "Activates your deep core stabilizers, building a foundation of support for your lower back." },
      { name: "Bird-Dog", sets: 2, reps: 8, hold: 5, rationale: "Trains coordinated core control in a low-load position, improving stability without stressing your back.", performance_side: "Left" },
      { name: "Bird-Dog", sets: 2, reps: 8, hold: 5, rationale: "Trains coordinated core control in a low-load position, improving stability without stressing your back.", performance_side: "Right" },
      { name: "Glute Bridge", sets: 2, reps: 10, hold: null, rationale: "Strengthens your glutes and posterior chain, reducing load on your lower back during daily activities." },
      { name: "Supine Knee-to-Chest Stretch", sets: 1, reps: 4, hold: 15, rationale: "Gently stretches your lower back and hip flexors, helping relieve tension." },
    ],
  },
  HIP: {
    score: 45,
    readinessModifier: 85,
    estimatedTime: "15-20",
    exercises: [
      { name: "Seated Hip Circles", sets: 2, reps: 10, hold: null, rationale: "Promotes hip joint mobility and synovial fluid circulation to reduce stiffness." },
      { name: "Standing Hip Flexor Stretch", sets: 2, reps: 4, hold: 20, rationale: "Lengthens the hip flexors that tighten from prolonged sitting.", performance_side: "Left" },
      { name: "Standing Hip Flexor Stretch", sets: 2, reps: 4, hold: 20, rationale: "Lengthens the hip flexors that tighten from prolonged sitting.", performance_side: "Right" },
      { name: "Clamshell", sets: 2, reps: 12, hold: null, rationale: "Strengthens hip external rotators and abductors to improve pelvic stability.", performance_side: "Left" },
      { name: "Clamshell", sets: 2, reps: 12, hold: null, rationale: "Strengthens hip external rotators and abductors to improve pelvic stability.", performance_side: "Right" },
      { name: "Glute Bridge", sets: 2, reps: 10, hold: null, rationale: "Strengthens your glutes to better support hip joint mechanics." },
    ],
  },
  THIGH: {
    score: 48,
    readinessModifier: 85,
    estimatedTime: "15-20",
    exercises: [
      { name: "Seated Quad Set", sets: 2, reps: 10, hold: 5, rationale: "Activates your quadriceps in a low-load position to build foundational thigh strength." },
      { name: "Standing Hamstring Curl", sets: 2, reps: 10, hold: null, rationale: "Strengthens the hamstrings to balance forces across the thigh.", performance_side: "Left" },
      { name: "Standing Hamstring Curl", sets: 2, reps: 10, hold: null, rationale: "Strengthens the hamstrings to balance forces across the thigh.", performance_side: "Right" },
      { name: "Standing IT Band Stretch", sets: 2, reps: 4, hold: 20, rationale: "Reduces tension along the outer thigh to alleviate friction at the knee.", performance_side: "Left" },
      { name: "Standing IT Band Stretch", sets: 2, reps: 4, hold: 20, rationale: "Reduces tension along the outer thigh to alleviate friction at the knee.", performance_side: "Right" },
      { name: "Seated Hip Hinge", sets: 2, reps: 10, hold: null, rationale: "Trains proper hip hinge mechanics to reduce compensatory thigh loading." },
    ],
  },
  KNEE: {
    score: 40,
    readinessModifier: 85,
    estimatedTime: "15-20",
    exercises: [
      { name: "Seated Isometric Quad Hold", sets: 2, reps: 8, hold: 10, rationale: "Builds quadriceps strength without stressing the knee joint through range of motion." },
      { name: "Seated Knee Extension", sets: 2, reps: 10, hold: null, rationale: "Strengthens the quadriceps to improve patellar tracking and knee stability." },
      { name: "Standing Calf Raise", sets: 2, reps: 12, hold: null, rationale: "Strengthens the lower leg muscles that support knee stability during walking and stairs." },
      { name: "Seated Hip Abduction", sets: 2, reps: 10, hold: null, rationale: "Strengthens the hip abductors to prevent knee valgus collapse during functional movements." },
      { name: "Wall Sit", sets: 2, reps: 4, hold: 15, rationale: "Builds isometric quadriceps endurance to support knee function during daily activities." },
      { name: "Standing Hamstring Curl", sets: 2, reps: 10, hold: null, rationale: "Balances quadriceps-hamstring strength ratio for overall knee joint stability.", performance_side: "Left" },
    ],
  },
};

export function getDummyPlan(problemArea) {
  return DUMMY_PLANS[problemArea] || DUMMY_PLANS.LBP;
}
